// useAuthSession
// --------------
// The user-triggered auth actions extracted out of portal.jsx: login (with its
// MFA gating) and logout. State-bag factory (same pattern as
// useRequestPersistence): it receives the App's setters + loaders and returns
// { login, logout }. The session-restore effect + onAuthStateChange subscription
// stay in portal.jsx (thin glue over the already-tested loadSessionUser).
//
// TypeScript source -- useAuthSession.js is a thin re-export stub.

import { canAccess, isPickerJob, isResponderJob } from "../app/access";
import { logger } from "../utils/logger";
import { isMfaGatedRole, needsMfaEnrollment } from "../utils/mfa";
import { defaultPageForUser } from "../utils/sessionUser";
import { getSupabaseClient } from "../utils/supabase";

interface AuthUserArg {
  role?: string | null;
  sessionMode?: string | null;
  email?: string;
  sessionReady?: boolean;
}

export interface AuthSessionDeps {
  setUser: (u: AuthUserArg | null) => void;
  setSel: (v: unknown) => void;
  setNewReq: (v: boolean) => void;
  setEditingDraft: (v: unknown) => void;
  setMfaRequired: (v: boolean) => void;
  setMfaChallenge: (v: boolean) => void;
  setPage: (v: string) => void;
  setRequests: (v: unknown) => void;
  setTodoReturnPage: (v: unknown) => void;
  loadRequests: (u: AuthUserArg) => void;
  loadUnactionedTasks: () => void;
  loadPickerTasks: () => void;
  loadTopUpTasks: () => void;
}

const MFA_REMEMBER_DAYS = 30;

export function useAuthSession(deps: AuthSessionDeps) {
  const {
    setUser,
    setSel,
    setNewReq,
    setEditingDraft,
    setMfaRequired,
    setMfaChallenge,
    setPage,
    setRequests,
    setTodoReturnPage,
    loadRequests,
    loadUnactionedTasks,
    loadPickerTasks,
    loadTopUpTasks,
  } = deps;

  async function login(u: AuthUserArg) {
    const isStaffRole = isMfaGatedRole(u.role);

    if (isStaffRole) {
      const supabaseClient = getSupabaseClient();
      if (supabaseClient) {
        // Check MFA status for staff -- require enrollment, challenge if enrolled.
        // The enrolment check is shared with the session-restore path (portal.jsx)
        // via needsMfaEnrollment(), so MFA can't be bypassed by arriving through a
        // recovery/email link or a refresh instead of an explicit login.
        try {
          if (await needsMfaEnrollment(supabaseClient)) {
            // Staff must set up MFA before proceeding.
            setUser(u);
            setMfaRequired(true);
            return;
          }

          const { data: assurance } =
            await supabaseClient.auth.mfa.getAuthenticatorAssuranceLevel();
          if (assurance && assurance.currentLevel !== assurance.nextLevel) {
            // Enrolled but not challenged this session -- honour the 30-day
            // "remember this device" grace period before forcing a code.
            const mfaKey = `zephyr_mfa_${u.email}`;
            let lastVerified: string | null = null;
            try {
              lastVerified = localStorage.getItem(mfaKey);
            } catch (_e) {}
            let withinGrace = false;
            if (lastVerified) {
              const elapsed = Date.now() - parseInt(lastVerified, 10);
              withinGrace = elapsed < MFA_REMEMBER_DAYS * 24 * 60 * 60 * 1000;
            }
            if (!withinGrace) {
              setUser(u);
              setMfaChallenge(true);
              return;
            }
          }
        } catch (e) {
          logger.warn("MFA check failed:", e);
          // Don't block login if the MFA check errors.
        }
      }
    }

    setUser(u);
    setSel(null);
    setNewReq(false);
    setEditingDraft(null);
    // Combined-role / multi-shelter users go through PostLoginSelection first.
    if (u.sessionReady === false) return;
    setPage(defaultPageForUser(u));
    loadRequests(u);
    // Eager-load the task-list badges this user is entitled to. Mirror the
    // session-restore and post-login paths in portal.jsx (all unified on the
    // access.ts predicates): a pure Picker was previously excluded here by the
    // `role !== PICKER` gate, and the top-up badge never loaded on a fresh login.
    if (isResponderJob(u)) loadUnactionedTasks();
    if (isPickerJob(u)) loadPickerTasks();
    if (canAccess(u, "top_up_tasks")) loadTopUpTasks();
  }

  async function logout() {
    const supabaseClient = getSupabaseClient();
    if (supabaseClient) await supabaseClient.auth.signOut();
    // Clear persisted UI state (e.g. task-list filters) so the next user lands
    // on default views, not the previous user's.
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.indexOf("zephyr_") === 0) keys.push(k);
      }
      for (const k of keys) sessionStorage.removeItem(k);
    } catch (_e) {}
    setUser(null);
    setPage("dashboard");
    setSel(null);
    setNewReq(false);
    setRequests([]);
    setTodoReturnPage(null);
  }

  return { login, logout };
}
